import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Chip,
  Paper,
  Fade,
  Zoom,
  Grid,
  IconButton,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    padding: 0,
    maxWidth: 500,
    position: "relative",
  },
}));

const OptionCard = styled(Paper)(({ theme, selected, color }) => ({
  position: "relative",
  padding: "8px 16px",
  cursor: "pointer",
  border: `2px solid ${
    selected ? theme.palette[color].main : theme.palette.divider
  }`,
  backgroundColor: selected ? alpha(theme.palette[color].main, 0.08) : "white",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "visible",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    borderColor: theme.palette[color].main,
    backgroundColor: alpha(theme.palette[color].main, 0.08),
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },
  "& .icon-container": {
    transition: "transform 0.3s ease",
  },
  "&:hover .icon-container": {
    transform: "scale(1.1)",
  },
  "& .underline": {
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
    borderRadius: 2,
    marginTop: "8px",
    transform: "scaleX(0)",
    transition: "transform 0.3s ease",
  },
  "&:hover .underline": {
    transform: "scaleX(1)",
  },
}));

const IconContainer = styled(Box)(({ theme, gradient }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(1.5),
  background: gradient,
}));

const SparkleContainer = styled(Box)({
  position: "absolute",
  top: -6,
  right: -6,
  "& .sparkle-main": {
    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  "& .sparkle-ping": {
    position: "absolute",
    inset: 0,
    animation: "ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite",
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.5 },
  },
  "@keyframes ping": {
    "75%, 100%": { transform: "scale(1.5)", opacity: 0 },
  },
});

const AIPoweredChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(90deg, #8a3ffc 0%, #c43af3 50%, #ff2e74 100%)`,
  color: "white",
  fontWeight: 600,
  fontSize: "0.625rem",
  height: 22,
  marginBottom: "8px",
  "& .MuiChip-icon": {
    color: "white",
    fontSize: "0.875rem",
  },
}));

export function AddJobSelectionModal({ open, onClose, onSelect }) {
  const [selectedOption, setSelectedOption] = useState(null);

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setTimeout(() => {
      onSelect(option);
      setSelectedOption(null);
    }, 600);
  };

  const handleClose = () => {
    setSelectedOption(null);
    onClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 12,
          top: 10,
          color: "white",
          zIndex: 2,
          "&:hover": { color: "#f0f0f0" },
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle
        disableTypography
        sx={{
          pt: 2,
          pb: 1,
          background: "linear-gradient(135deg, #b388eb 0%, #f9c5d1 100%)",
          color: "#fff",
          textAlign: "center",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          Create New Job
        </Typography>
        <Typography variant="body2" color="#fff">
          Choose how you'd like to create your job posting
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* AI Generate Option */}
          <Grid item xs={12} sm={6}>
            <Zoom in={open} style={{ transitionDelay: "100ms" }}>
              <OptionCard
                elevation={selectedOption === "ai" ? 8 : 1}
                selected={selectedOption === "ai"}
                color="primary"
                onClick={() => handleSelectOption("ai")}
              >
                <SparkleContainer>
                  <AutoAwesomeIcon
                    className="sparkle-main"
                    sx={{ fontSize: 24, color: "primary.main" }}
                  />
                  <AutoAwesomeIcon
                    className="sparkle-ping"
                    sx={{
                      fontSize: 24,
                      color: "primary.main",
                      opacity: 0.75,
                    }}
                  />
                </SparkleContainer>

                <Box sx={{ minHeight: 22 }}>
                  <AIPoweredChip
                    icon={<AutoFixHighIcon />}
                    label="AI POWERED"
                    size="small"
                  />
                </Box>

                <IconContainer
                  className="icon-container"
                  gradient="linear-gradient(135deg, #f3e5f5 0%, #fce4ec 100%)"
                >
                  <AutoAwesomeIcon
                    sx={{ fontSize: 24, color: "primary.main" }}
                  />
                </IconContainer>

                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  gutterBottom
                  align="center"
                >
                  Generate with AI
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  fontSize="0.8rem"
                >
                  Let AI create a professional job posting in seconds.
                </Typography>
                <Box className="underline" />
              </OptionCard>
            </Zoom>
          </Grid>

          {/* Manual Create Option */}
          <Grid item xs={12} sm={6}>
            <Zoom in={open} style={{ transitionDelay: "200ms" }}>
              <OptionCard
                elevation={selectedOption === "manual" ? 8 : 1}
                selected={selectedOption === "manual"}
                color="primary"
                onClick={() => handleSelectOption("manual")}
              >
                <Box sx={{ minHeight: 22, mb: "8px" }} />
                <IconContainer
                  className="icon-container"
                  gradient="linear-gradient(135deg, #e3f2fd 0%, #e0f7fa 100%)"
                >
                  <EditNoteIcon sx={{ fontSize: 24, color: "primary.main" }} />
                </IconContainer>

                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  gutterBottom
                  align="center"
                >
                  Create Manually
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  fontSize="0.8rem"
                >
                  Fill out the job posting form yourself with full control.
                </Typography>
                <Box className="underline" />
              </OptionCard>
            </Zoom>
          </Grid>
        </Grid>
      </DialogContent>
    </StyledDialog>
  );
}
